/*
 * Copyright (c) 2016 Omnigon Communications, LLC. All rights reserved.
 *
 * This software is the confidential and proprietary information of Omnigon Communications, LLC
 * ("Confidential Information"). You shall not disclose such Confidential Information and shall access and use it only
 * in accordance with the terms of the license agreement you entered into with Omnigon Communications, LLC, its
 * subsidiaries, affiliates or authorized licensee. Unless required by applicable law or agreed to in writing, this
 * Confidential Information is provided on an "AS IS" BASIS, WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either
 * express or implied. See the license agreement for the specific language governing permissions and limitations.
 */

import _ from 'lodash';
import Map from 'scripts/models/base/map';
import appConfig from 'scripts/app-config';

const resizeTimeout = 100;
const scrollTimeout = 100;

export default Map.extend({}, {

    init: function () {
        this.screenSizes = _.get(appConfig, 'screen-sizes');
        this._initScreenSizeCheckMethods();
        this.bind('screenDimensions', this.onWindowResize.bind(this));
        this.bind('screenOrientation', this.onScreenOrientation.bind(this));
        this.bind('documentDimensions', this.onDocumentResize.bind(this));
        this.bind('scrollOffset', this.onWindowScroll.bind(this));
        this.bind('screenSize', this.onLayout.bind(this));
        this.bind('documentReady', this.onDocumentReady.bind(this));
        this.bind('windowLoad', this.onWindowLoad.bind(this));
        this.onWindowResize();
    },

    _initScreenSizeCheckMethods: function () {
        if (this.screenSizes) {
            _.forOwn(this.screenSizes, function (value, key) {
                var methodName = 'is' + this._getCheckSizeMethodName(key);
                if (methodName) {
                    this[methodName] = this._checkScreenSize.bind(this, value);
                }
            }.bind(this));
        }
    },

    _getCheckSizeMethodName: function (key) {
        var nameParts = _.split(key, '-');
        if (nameParts.length > 0) {
            nameParts = _.map(nameParts, part => _.capitalize(part));
            return _.join(nameParts, '');
        }
        return null;
    },

    _checkScreenSize: function (size) {
        var width = this.screenDimensions.width;
        return width < size.max && width >= size.min;
    },

    _getScreenSize: function () {
        return _.findKey(this.screenSizes, size => this._checkScreenSize(size));
    },

    onWindowResize: function () {
        this.attr('screenSize', this._getScreenSize());

        this.dispatch('onWindowResizeNow');
        this.deferredWindowResize();
    },

    deferredWindowResize: _.throttle(function () {
        this.dispatch('onWindowResize');
    }, resizeTimeout),

    onDocumentResize: function () {
        this.dispatch('onDocumentResizeNow');
        this.deferredDocumentResize();
    },

    deferredDocumentResize: _.throttle(function () {
        this.dispatch('onDocumentResize');
    }, resizeTimeout),

    onScreenOrientation() {
        this.dispatch('onScreenOrientation', [this.attr('screenOrientation')]);
    },

    onWindowScroll: function () {
        this.dispatch('onWindowScrollNow');
        this.deferredWindowScroll();
    },    

    deferredWindowScroll: _.throttle(function () {
        this.dispatch('onWindowScroll', [this.attr('scrollOffset')]);
    }, scrollTimeout),

    onLayout: function () {
        this.dispatch('onLayout', [this.attr('screenSize')]);
    },

    onDocumentReady: function () {
        this.dispatch('onDocumentReady');
    },

    onWindowLoad: function () {
        this.dispatch('onWindowLoad');
    },

    define: {
        screenDimensions: {
            type: 'object',
            value: {}
        },
        documentDimensions: {
            type: 'object',
            value: {}
        },
        scrollOffset: {
            type: 'number',
            value: 0
        },
        scrollDirection: {
            type: 'string',
            value: 'never'
        },
        screenSize: {
            type: 'string',
            value: ''
        },
        screenOrientation: {
            type: 'string',
            value: ''
        },
        documentReady: {
            type: 'boolean',
            value: false
        },
        windowLoad: {
            type: 'boolean',
            value: false
        }
    }
});
